import { useEffect, useState } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import Icon from "./Icon";
import SessionStudent from "@utils/SessionStudent";
import T, { GetLang } from "./translations/Translations";

import { DASH_LINKS } from "@const/AppCardLinks";

import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import type { Student, StudentImage, StudentOe } from "@/types/GlobalState";

interface StudentCarouselProps {
    addAllowed: boolean;
    setExpand: (b: boolean) => void;
    setSelectedStudent: (s: Student | StudentOe | null) => void;
    studentImageList: StudentImage[];
    studentList: (Student | StudentOe)[];
}

const resolveStudent = (s: Student | StudentOe): Student => ("studentDto" in s ? (s.studentDto as Student) : s);

/**
 * Display a selectable list of student pictures
 * @name StudentCarousel
 */
const StudentCarousel = ({
    addAllowed,
    setExpand,
    setSelectedStudent,
    studentImageList,
    studentList
}: StudentCarouselProps): React.JSX.Element => {
    const [index, setIndex] = useState(0);
    const lang = GetLang();

    const tAdd = T({ tKey: "Global_Add_New_Student", langStr: lang });
    const tAddTooltip = T({ tKey: "Global_Add_New_Student_For_Open_Enrollment", langStr: lang });

    /**
     * Direct the user to the OE home page
     * @name handleAdd
     */
    const handleAdd = (): void => {
        globalThis.location.assign(`${DASH_LINKS.open_enrollment}/home`);
    };

    /**
     * Select the next student in the list or start from beginning if at end
     * @name handleNext
     */
    const handleNext = (): void => {
        if (index < studentList?.length - 1) {
            const next = index + 1;
            setIndex(next);
            setSelectedStudent(studentList[next]);
            SessionStudent(resolveStudent(studentList[next]), "sessionStudent");
        } else {
            setIndex(0);
            setSelectedStudent(studentList[0]);
            SessionStudent(resolveStudent(studentList[0]), "sessionStudent");
        }
        setExpand(false);
    };

    /**
     * Select the previous student in the list or cycle to the end if at beginning
     * @name handlePrev
     */
    const handlePrev = (): void => {
        if (index > 0) {
            const prev = index - 1;
            setIndex(prev);
            setSelectedStudent(studentList[prev]);
            SessionStudent(resolveStudent(studentList[prev]), "sessionStudent");
        } else {
            setIndex(studentList.length - 1);
            setSelectedStudent(studentList.at(-1) ?? null);
            SessionStudent(studentList.at(-1) ? resolveStudent(studentList.at(-1)!) : null, "sessionStudent");
        }
        setExpand(false);
    };

    const handleSelect = (selectedIndex: number): void => {
        setIndex(selectedIndex);
        setSelectedStudent(studentList[selectedIndex]);
        setExpand(false);
        SessionStudent(resolveStudent(studentList[selectedIndex]), "sessionStudent");
    };

    const renderTooltip = (props: OverlayInjectedProps): JSX.Element => (
        <Tooltip id="button-tooltip" {...props}>
            {tAddTooltip}
        </Tooltip>
    );

    useEffect(() => {
        if (!studentList || studentList.length <= 1) {
            // Set initial state if list is empty or has only one item
            if (studentList?.length === 1) {
                setIndex(0);
                setSelectedStudent(studentList[0]);
            }

            return;
        }

        const sessionStudentId = sessionStorage.getItem("sessionStudent");

        if (sessionStudentId) {
            const sessionStudentIndex = studentList.findIndex((student) => {
                const resolved = resolveStudent(student);
                const studentKeyString = String(resolved.studentKey);
                const studentNumberString = String(resolved.studentNumber);

                return studentKeyString === sessionStudentId || studentNumberString === sessionStudentId;
            });

            if (sessionStudentIndex === -1) {
                // ID found in session storage but not in current list
                setIndex(0);
                setSelectedStudent(studentList[0]);
            } else {
                setIndex(sessionStudentIndex);
                setSelectedStudent(studentList[sessionStudentIndex]);
            }
        } else {
            // No ID in session storage, default to the first student
            setIndex(0);
            setSelectedStudent(studentList[0]);
        }
    }, [setSelectedStudent, studentList]);

    return (
        <div className="d-flex align-items-center justify-content-center">
            {studentList?.length > 1 && (
                <button className="carousel-button" onClick={handlePrev}>
                    <Icon fill="#0669B3" height={30} iconName="LEFT_ARROW" width={30} />
                </button>
            )}
            <div className="d-flex">
                <div className="student-icon-container">
                    {studentList?.map((student, i) => {
                        const studentImage = studentImageList?.find(
                            (obj) =>
                                Number.parseInt(obj.studentNumber, 10) ===
                                Number.parseInt(resolveStudent(student).studentNumber, 10)
                        );
                        const uniqueKey = `carousel-ic-${i}`;
                        if (studentImage?.imageUrl) {
                            return (
                                <button
                                    className="rounded-2xl mx-1 mb-1"
                                    key={uniqueKey}
                                    onClick={() => handleSelect(i)}
                                    style={{
                                        alignItems: "center",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "90px",
                                        margin: "0 5px 0 5px",
                                        objectFit: "cover",
                                        opacity: i === index ? "1" : "0.5",
                                        padding: 0,
                                        transform: i === index ? "scale(1.05)" : "scale(1)",
                                        transition: "all 0.5s ease-in-out",
                                        width: "70px"
                                    }}
                                >
                                    <img
                                        alt={`${resolveStudent(student).firstName} ${resolveStudent(student).lastName}`}
                                        key={uniqueKey}
                                        src={studentImage.imageUrl}
                                        style={{
                                            borderRadius: "30px",
                                            height: "60px",
                                            objectFit: "cover",
                                            opacity: i === index ? "1" : "0.5",
                                            transform: i === index ? "scale(1.05)" : "scale(1)",
                                            transition: "all 0.5s ease-in-out",
                                            width: "60px"
                                        }}
                                    />
                                    <span className="carousel-name">{resolveStudent(student).firstName}</span>
                                    {i === index && <div className="blue-bar">&nbsp;</div>}
                                </button>
                            );
                        }
                        return (
                            <button
                                className="rounded-2xl mx-1 mb-1"
                                key={uniqueKey}
                                onClick={() => handleSelect(i)}
                                style={{
                                    alignItems: "center",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "90px",
                                    margin: "0 5px 0 5px",
                                    objectFit: "cover",
                                    opacity: i === index ? "1" : "0.5",
                                    padding: 0,
                                    transform: i === index ? "scale(1.05)" : "scale(1)",
                                    transition: "all 0.5s ease-in-out",
                                    width: "70px"
                                }}
                            >
                                <div>
                                    <Icon
                                        fill="#19608F"
                                        height={60}
                                        iconName="NO_STUDENT_IMAGE"
                                        key={uniqueKey}
                                        width={60}
                                    />
                                </div>
                                <span className="carousel-name">{resolveStudent(student).firstName}</span>
                                {i === index && <div className="blue-bar">&nbsp;</div>}
                            </button>
                        );
                    })}
                </div>
                {addAllowed && (
                    <OverlayTrigger placement="top" overlay={renderTooltip}>
                        <button
                            className="icon-button mb-3"
                            onClick={handleAdd}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                        >
                            <div>
                                <Icon fill="#707070" height={60} iconName="ADD" width={60} />
                            </div>
                            <span className="carousel-name">{tAdd}</span>
                        </button>
                    </OverlayTrigger>
                )}
            </div>
            {studentList?.length > 1 && (
                <button className="carousel-button" onClick={handleNext}>
                    <Icon fill="#0669B3" height={30} iconName="RIGHT_ARROW" width={30} />
                </button>
            )}
        </div>
    );
};

export default StudentCarousel;
